import { useMapGetter } from 'dashboard/composables/store';

// URL do webhook receiver do Agent Bot LAD (bot-server.js), injetada em
// window.ladBotServerUrl pelo vueapp.html.erb a partir da env var
// LAD_BOT_SERVER_URL do container do Chatwoot. Sem default de localhost de
// proposito: este fetch roda no navegador do ATENDENTE, entao o default
// apontaria pra maquina DELE - em producao vira falha silenciosa (ou pior,
// acerta algum outro servico que ele tenha na 3901). Vazia = os dois botoes
// respondem com erro explicito.
const BOT_SERVER_URL = (window.ladBotServerUrl || '').replace(/\/+$/, '');

// fetch nao tem timeout proprio - sem isto o botao gira pra sempre quando o
// bot nao responde. O corte tem que ficar ACIMA do maior tempo legitimo:
// /agent/transfer-to-bot espera a fila por conversa do bot e /agent/ask com
// "/codigo" pode levar ~90s (timeout do internal-tools-bot).
const TIMEOUT_MS = 120000;

const ERRO_SEM_URL =
  'Bot LAD nao configurado nesta instalacao (LAD_BOT_SERVER_URL vazia no container do Chatwoot).';
const ERRO_TIMEOUT =
  'O Bot LAD nao respondeu a tempo. Tente de novo em alguns instantes.';
const ERRO_REDE = 'Nao foi possivel falar com o Bot LAD.';

/**
 * Composable de rede para o Agent Bot LAD (nosso, fora do Captain/enterprise).
 * Sem estado de UI - isso fica no useCopilotReply.js, que ja sabe gerenciar
 * showEditor/generatedContent/accept().
 */
export function useLadBot() {
  const currentUser = useMapGetter('getCurrentUser');

  async function callBotServer(path, body, signal) {
    if (!BOT_SERVER_URL) {
      return {
        ok: false,
        data: { error: ERRO_SEM_URL, errorType: 'nao_configurado' },
      };
    }

    // AbortController proprio pra somar o timeout ao abort de quem chamou (o
    // cancelamento do painel de rascunho). AbortSignal.any/AbortSignal.timeout
    // ainda nao da pra assumir nos navegadores dos atendentes.
    const controller = new AbortController();
    const abortarPeloChamador = () => controller.abort();
    let expirou = false;
    const timer = setTimeout(() => {
      expirou = true;
      controller.abort();
    }, TIMEOUT_MS);
    if (signal) {
      if (signal.aborted) controller.abort();
      else signal.addEventListener('abort', abortarPeloChamador, { once: true });
    }

    try {
      const response = await fetch(`${BOT_SERVER_URL}${path}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...body,
          agentApiToken: currentUser.value?.access_token,
        }),
        signal: controller.signal,
      });
      let data = {};
      try {
        data = await response.json();
      } catch (e) {
        // resposta sem corpo/nao-JSON; segue com data={}
      }
      return { ok: response.ok, data };
    } catch (e) {
      if (expirou) {
        return { ok: false, data: { error: ERRO_TIMEOUT, errorType: 'timeout' } };
      }
      // Cancelamento pedido pelo atendente continua subindo: o useCopilotReply
      // ja trata esse caso e ele nao pode virar alerta de erro na tela.
      if (signal && signal.aborted) throw e;
      // Rede/CORS/mixed-content viram erro tratado em vez de excecao solta -
      // transferToBot e chamado sem try/catch do outro lado.
      return { ok: false, data: { error: ERRO_REDE, errorType: 'api_error' } };
    } finally {
      clearTimeout(timer);
      if (signal) signal.removeEventListener('abort', abortarPeloChamador);
    }
  }

  async function ask(conversationId, pergunta, { signal } = {}) {
    const { ok, data } = await callBotServer(
      '/agent/ask',
      { conversationId, pergunta },
      signal
    );
    if (!ok) {
      return {
        message: '',
        errorType: data.errorType || 'api_error',
        errorMessage: data.error || ERRO_REDE,
      };
    }
    if (data.tipo === 'sem_evidencia') {
      return { message: '', errorType: 'sem_evidencia' };
    }
    return { message: data.texto };
  }

  async function transferToBot(conversationId) {
    const { ok, data } = await callBotServer('/agent/transfer-to-bot', {
      conversationId,
    });
    return { ok, error: ok ? null : data.error || ERRO_REDE };
  }

  return { ask, transferToBot };
}
